import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  Search, 
  Link2, 
  FileText, 
  Activity, 
  BarChart3, 
  TestTube2,
  Upload,
  LayoutDashboard
} from 'lucide-react';
import { KeywordResearch } from './seo/KeywordResearch';
import { BacklinkMonitoring } from './seo/BacklinkMonitoring';
import { ContentOptimization } from './seo/ContentOptimization';
import { TechnicalAudit } from './seo/TechnicalAudit';
import { PerformanceTracking } from './seo/PerformanceTracking';
import { ABTesting } from './seo/ABTesting';
import { ScheduleManager } from './seo/ScheduleManager';
import { RealTimeMetrics } from './seo/RealTimeMetrics';
import { BulkSEOImport } from './BulkSEOImport';
import { SEOAutomationDashboard } from './SEOAutomationDashboard';
import { useAuth } from '@/hooks/useAuth';

export const SEOAutomation = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleImportComplete = () => {
    toast({
      title: 'Import Complete',
      description: 'SEO keywords have been processed and indexed',
    });
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            SEO Automation System
          </CardTitle>
          <CardDescription>
            Comprehensive automated SEO tools for keyword research, backlink monitoring, 
            content optimization, technical audits, and performance tracking
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-2 lg:grid-cols-10 gap-2">
              <TabsTrigger value="dashboard" className="flex items-center gap-2">
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="bulk-import" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Bulk Import
              </TabsTrigger>
              <TabsTrigger value="metrics" className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Metrics
              </TabsTrigger>
              <TabsTrigger value="schedule" className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Schedule
              </TabsTrigger>
              <TabsTrigger value="keywords" className="flex items-center gap-2">
                <Search className="w-4 h-4" />
                Keywords
              </TabsTrigger>
              <TabsTrigger value="backlinks" className="flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Backlinks
              </TabsTrigger>
              <TabsTrigger value="content" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Content
              </TabsTrigger>
              <TabsTrigger value="technical" className="flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Technical
              </TabsTrigger>
              <TabsTrigger value="performance" className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Reports
              </TabsTrigger>
              <TabsTrigger value="abtesting" className="flex items-center gap-2">
                <TestTube2 className="w-4 h-4" />
                A/B Tests
              </TabsTrigger>
            </TabsList>

            <TabsContent value="dashboard" className="mt-6">
              {user && <SEOAutomationDashboard key={refreshKey} userId={user.id} />}
            </TabsContent>

            <TabsContent value="bulk-import" className="mt-6">
              {user && <BulkSEOImport userId={user.id} onImportComplete={handleImportComplete} />}
            </TabsContent>

            <TabsContent value="metrics" className="mt-6">
              <RealTimeMetrics />
            </TabsContent>

            <TabsContent value="schedule" className="mt-6">
              <ScheduleManager />
            </TabsContent>

            <TabsContent value="keywords" className="mt-6">
              <KeywordResearch />
            </TabsContent>

            <TabsContent value="backlinks" className="mt-6">
              <BacklinkMonitoring />
            </TabsContent>

            <TabsContent value="content" className="mt-6">
              <ContentOptimization />
            </TabsContent>

            <TabsContent value="technical" className="mt-6">
              <TechnicalAudit />
            </TabsContent>

            <TabsContent value="performance" className="mt-6">
              <PerformanceTracking />
            </TabsContent>

            <TabsContent value="abtesting" className="mt-6">
              <ABTesting />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};